import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Link } from "react-router-dom";
import { SectionHeading } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { PrimaryButton } from "components/misc/Buttons.js";
import { Container } from "components/misc/Layouts.js";
import { Eyebrow, HighlightedText, ArrowLink, VisuallyHidden } from "blog/components/ui.js";
import { ReactComponent as ArrowRightIcon } from "feather-icons/dist/icons/arrow-right.svg";
import { ReactComponent as SvgDotPattern } from "images/dot-pattern.svg";
import professionalIllustration from "images/professional-illustration.svg";
import { GRETA_FORMATION_IA_URL } from "home/data/acteurs.js";

/**
 * Section « Se former » de la page d’accueil (id="formation").
 *
 * Un parcours en trois étapes pour se former à l’IA dans le bassin de Besançon.
 * Le blog étant le cœur du site, le bouton principal mène à la catégorie
 * « Formation & emploi » ; la fiche officielle de la formation Développeur en
 * intelligence artificielle du GRETA-CFA Franche-Comté est le lien secondaire
 * (externe, nouvel onglet signalé). Squelette repris de
 * components/features/TwoColWithSteps.js, avec titres d’étapes en h3 et liste
 * ordonnée. Aucune image externe. Primitives de blog/components/ui.js.
 */

/* Mise en page : 5/12 image, 7/12 texte (trois étapes + deux actions ont besoin de place dès md). */
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-20 md:py-24 items-center`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 relative md:order-last md:ml-12 lg:ml-16`;
const TextColumn = tw(Column)`md:w-7/12 mt-16 md:mt-0`;

/* professional-illustration.svg : 584 × 652 (espace réservé avant chargement). */
const Illustration = tw.img`w-full`;
const DecoratorBlob = tw(
  SvgDotPattern
)`w-20 h-20 absolute right-0 bottom-0 transform translate-x-1/2 translate-y-1/2 fill-current text-primary-500 opacity-25 -z-10`;

const TextContent = tw.div`lg:py-8 text-center md:text-left`;
const Subheading = tw(Eyebrow)`block`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-3xl sm:text-4xl text-center md:text-left leading-tight`;
/* secondary-300 : même bleu nuit que la DA, mais contraste AA sur fond blanc. */
const Description = tw(SectionDescription)`mt-4 mx-auto md:mx-0 text-secondary-300`;

/* Étapes : liste ordonnée (role="list" car list-none), numéro décoratif en gray-400 comme la DA ;
   le rang est donné aux lecteurs d’écran par un texte masqué dans le h3. */
const Steps = tw.ol`mt-12 list-none`;
const Step = tw.li`mt-8 flex flex-col md:flex-row items-center md:items-start`;
const StepNumber = tw.div`font-semibold text-4xl leading-none text-gray-400`;
const StepText = tw.div`mt-3 md:mt-0 md:ml-6`;
const StepHeading = tw.h3`leading-none text-xl font-semibold`;
/* Même gris que le texte des PostCards. */
const StepDescription = tw.p`mt-3 max-w-xs mx-auto md:mx-0 leading-loose text-sm text-gray-600 font-medium`;

/* Actions empilées jusqu’à lg (la colonne est trop étroite à md pour deux libellés côte à côte),
   retour à la ligne autorisé ; bouton au gabarit de la DA (px-8 py-4). */
const Actions = tw.div`mt-10 flex flex-col lg:flex-row flex-wrap items-center md:items-start justify-center md:justify-start`;
const MainButton = tw(PrimaryButton)`inline-block px-8 py-4 w-full sm:w-auto text-center`;
/* Anneau de focus ajouté localement : ArrowLink (ui.js, lecture seule) n’a qu’un soulignement. */
const SecondaryLink = styled(ArrowLink)`
  ${tw`mt-4 lg:mt-0 lg:ml-8 rounded focus:shadow-outline`}
`;

const defaultSteps = [
  {
    heading: "Comprendre",
    description:
      "Nos articles expliquent l’IA en français clair, avec des exemples pris dans la région : de quoi se faire une idée juste avant de s’engager.",
  },
  {
    heading: "Choisir sa formation",
    description:
      "Plusieurs voies existent à Besançon : la formation « Développeur en intelligence artificielle » du GRETA-CFA Franche-Comté, ainsi que les cursus en informatique et en ingénierie de l’Université Marie et Louis Pasteur et de SUPMICROTECH-ENSMM.",
  },
  {
    heading: "Rejoindre l’écosystème",
    description:
      "Alternance, stage, premier emploi ou reconversion : la catégorie « Formation & emploi » rassemble nos guides et nos repères pour se lancer dans la région.",
  },
];

export default function SeFormer({
  subheading = "Se former",
  heading = (
    <>
      Apprendre l’IA <HighlightedText>sans quitter Besançon.</HighlightedText>
    </>
  ),
  description = "Trois étapes pour passer de la curiosité à la pratique, avec des ressources et des formations accessibles sur place.",
  steps = defaultSteps,
  /* Libellé court pour tenir sur une ligne dès md ; l’étape 3 nomme déjà « la catégorie ». */
  ctaText = "Lire « Formation & emploi »",
  ctaUrl = "/blog/categorie/formation-emploi",
  secondaryCtaText = "Voir la formation du GRETA",
  secondaryCtaUrl = GRETA_FORMATION_IA_URL,
  imageSrc = professionalIllustration,
}) {
  return (
    <Container id="formation">
      <TwoColumn>
        <ImageColumn>
          <Illustration src={imageSrc} alt="" width="584" height="652" loading="lazy" />
          <DecoratorBlob aria-hidden="true" focusable="false" />
        </ImageColumn>
        <TextColumn>
          <TextContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>{heading}</Heading>
            {description && <Description>{description}</Description>}
            <Steps role="list">
              {steps.map((step, index) => (
                <Step key={step.heading}>
                  <StepNumber aria-hidden="true">{(index + 1).toString().padStart(2, "0")}</StepNumber>
                  <StepText>
                    <StepHeading>
                      <VisuallyHidden>{`Étape ${index + 1} : `}</VisuallyHidden>
                      {step.heading}
                    </StepHeading>
                    <StepDescription>{step.description}</StepDescription>
                  </StepText>
                </Step>
              ))}
            </Steps>
            <Actions>
              <MainButton as={Link} to={ctaUrl}>
                {ctaText}
              </MainButton>
              {secondaryCtaText && secondaryCtaUrl && (
                <SecondaryLink as="a" href={secondaryCtaUrl} target="_blank" rel="noopener noreferrer">
                  {secondaryCtaText}
                  <VisuallyHidden>{" (s’ouvre dans un nouvel onglet)"}</VisuallyHidden>
                  <ArrowRightIcon aria-hidden="true" focusable="false" />
                </SecondaryLink>
              )}
            </Actions>
          </TextContent>
        </TextColumn>
      </TwoColumn>
    </Container>
  );
}
